import { FCM } from '@capacitor-community/fcm';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import axios from 'axios';
import router from '@/router';
import { parseInboundLocation } from '@/utils/publicUrl';
import { attachAuth } from '@/utils/authSession';
import { normalizeReceive, PERMISSION } from '@/utils/notificationPermission';

const apiAuthClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:9000/",
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
});

attachAuth(apiAuthClient);

let pushListenersReady = false;
let vueInstanceRef = null;

/**
 * Maps Xcode Debug vs Release/distribution to APNs environments.
 *
 * Installed @capacitor-community/fcm@7 does not expose setAPNSToken.
 * Firebase Messaging receives the APNs token natively from Capacitor
 * registration and uses the signed aps-environment:
 *   Debug  → App.debug.entitlements  → development → sandbox
 *   Release/TestFlight/App Store → App.release.entitlements → production
 */
export function apnsEnvironmentForIosBuild(isDebugBuild) {
  return isDebugBuild ? 'sandbox' : 'production';
}

export function devicePlatform() {
  return Capacitor.getPlatform();
}

export function isNativePushPlatform() {
  return Capacitor.isNativePlatform() && devicePlatform() !== 'web';
}

function navigateFromPushData(data) {
  const target = data?.articleUrl || data?.path;
  if (!target || typeof target !== 'string') return;
  const parsed = parseInboundLocation(target);
  if (!parsed) return;
  router.push(parsed.fullPath);
}

export function ensurePushListeners(vueInstance) {
  if (vueInstance) {
    vueInstanceRef = vueInstance;
  }
  if (pushListenersReady) return;
  pushListenersReady = true;
  if (!isNativePushPlatform()) return;

  PushNotifications.addListener('registration', () => {
    fetchFCMToken(vueInstanceRef);
  });

  PushNotifications.addListener('registrationError', (err) => {
    console.error('Error during push registration:', err);
  });

  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push notification received (foreground):', notification);
  });

  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('Push notification action performed:', action);
    navigateFromPushData(action.notification?.data);
  });
}

export async function checkPushPermission() {
  if (!isNativePushPlatform()) {
    return PERMISSION.UNSUPPORTED;
  }
  try {
    const result = await PushNotifications.checkPermissions();
    return normalizeReceive(result && result.receive, {
      native: true,
      platform: devicePlatform(),
    });
  } catch (err) {
    console.error('Error checking push permission:', err);
    return PERMISSION.UNSUPPORTED;
  }
}

export async function requestPushPermission() {
  if (!isNativePushPlatform()) {
    return PERMISSION.UNSUPPORTED;
  }
  ensurePushListeners(vueInstanceRef);
  const result = await PushNotifications.requestPermissions();
  const state = normalizeReceive(result && result.receive, {
    native: true,
    platform: devicePlatform(),
  });
  if (state === PERMISSION.GRANTED) {
    await PushNotifications.register();
  }
  return state;
}

/**
 * Explicit-intent entry. Call only after the user asks for notifications.
 * Listeners are attached at most once.
 */
export async function registerPushNotifications(vueInstance) {
  try {
    ensurePushListeners(vueInstance);
    const state = await requestPushPermission();
    if (state === PERMISSION.GRANTED) {
      await fetchFCMToken(vueInstance || vueInstanceRef);
    }
    return state;
  } catch (err) {
    console.error('Error during push notification registration:', err);
    return PERMISSION.DENIED;
  }
}

export async function registerPushIfGranted(vueInstance) {
  ensurePushListeners(vueInstance);
  const state = await checkPushPermission();
  if (state !== PERMISSION.GRANTED) return state;
  try {
    await PushNotifications.register();
    await fetchFCMToken(vueInstance || vueInstanceRef);
  } catch (err) {
    console.error('Error registering granted push:', err);
  }
  return state;
}

async function fetchFCMToken(vueInstance) {
  try {
    const result = await FCM.getToken();
    const fcmToken = result.token;
    const currentUser = vueInstance && vueInstance.$store
      ? vueInstance.$store.state.user
      : null;
    sendTokenToBackend(fcmToken, currentUser);
  } catch (err) {
    console.error('Error obtaining FCM token:', err);
  }
}

async function sendTokenToBackend(tokenValue, currentUser) {
  try {
    const storedToken = localStorage.getItem('token');
    if (!currentUser || !storedToken) {
      console.warn('User not authenticated (or no token). Skipping token registration.');
      return;
    }

    await apiAuthClient.post('/devices/registerToken', {
      token: tokenValue,
      platform: devicePlatform()
    });
  } catch (err) {
    console.error('Error sending token to backend:', err);
  }
}
